import useCreateSale from '@/hooks/queries/sale/useCreateSale';
import useGetServiceByIdOrCode from '@/hooks/queries/services/useGetServiceByIdOrCode';
import useGetUser from '@/hooks/queries/user/useGetUser';
import useMixPanel from '@/hooks/useMixPanel';
import { useRootStore } from '@/providers/root-store-provider';
import formParticipantDataWingfoilSchema from '@/schemas/formParticipantDataWingfoil';
import { TUserData } from '@/services/api/user-api';
import { Service } from '@/services/api/services-api';
import api from '@/services/api/api';
import { TOrganizationFAQ } from '@/services/api/organization-api';
import { useUser } from '@auth0/nextjs-auth0/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

type TFormLandingRental = {
  service: Service;
  user: TUserData | undefined;
};
type TForm = z.input<ReturnType<typeof formParticipantDataWingfoilSchema>>;

export const useFormLandingWingfoil = ({
  service,
  user
}: TFormLandingRental) => {
  const Mixpanel = useMixPanel();
  const [isOpenSummaryModal, setIsOpenSummaryModal] = useState(false);
  const [isSaleCreated, setIsSaleCreated] = useState(false);
  const cleanStepServicesData = useRootStore(
    (store) => store.cleanStepServicesData
  );

  const isDataExpired = useRootStore((store) => store.isDataExpired);
  const purchaseCompleted = useRootStore((store) => store.purchaseCompleted);
  const setPurchaseCompleted = useRootStore(
    (store) => store.setPurchaseCompleted
  );
  const { mutate, isPending: isPendingSale } = useCreateSale();

  const schema = formParticipantDataWingfoilSchema({
    minAge: service?.minAge,
    maxAge: service?.maxAge
  });

  const [isOpenRegisteredUserModal, setIsOpenRegisteredUserModal] =
    useState(false);

  const userEmailRef = useRef('');
  const emailHint = userEmailRef.current.replace(/\+/g, '%2B');
  const {
    data: isUserExists,
    isSuccess,
    refetch: checkUserEmail,
    dataUpdatedAt
  } = useQuery({
    queryKey: ['checkUser'],
    queryFn: () => api.user.userExistsByEmail(userEmailRef.current),
    enabled: false
  });

  const userConsumerData = user?.consumers?.find(
    (consumer) => consumer.isOwnUser
  );
  const defaultValuesUser = {
    firstName: userConsumerData?.firstName || '',
    lastName: userConsumerData?.lastName || '',
    phone: userConsumerData?.phone?.toString() || '',
    identification: {
      number: userConsumerData?.identification?.number || '',
      type: userConsumerData?.identification?.type || ''
    },
    email: userConsumerData?.email || '',
    address: userConsumerData?.address || '',
    location: userConsumerData?.location || '',
    postalCode: userConsumerData?.postalCode || '',
    birthdate: userConsumerData?.birthdate
      ? new Date(userConsumerData.birthdate)
      : undefined,
    illness: {
      value: userConsumerData?.additionalData?.illness?.value || false,
      answer: userConsumerData?.additionalData?.illness?.answer || ''
    }
  };

  const form = useForm<TForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      serviceDivisionGroups: [],
      userIsParticipant: true,
      user: defaultValuesUser,
      emergencyContact: {
        fullName: userConsumerData?.emergencyName || '',
        phone: userConsumerData?.emergencyPhone?.toString() || ''
      },
      participant: {
        id: '',
        firstName: '',
        lastName: '',
        identification: {
          number: '',
          type: ''
        },
        address: '',
        location: '',
        postalCode: '',
        birthdate: undefined,
        illness: {
          value: false,
          answer: ''
        }
      },
      acceptDataPrivacy: false,
      acceptTakePhotos: false,
      authOfferProducts: false
    },
    mode: 'onChange',
    shouldFocusError: true
  });

  const onSubmit = () => {
    Mixpanel.track(
      `[${service?.name}]: Create new sale after complete booking`,
      {
        screen: 'New Booking Landing',
        Activity: service?.name
      }
    );

    if (service) {
      const {
        userIsParticipant,
        user,
        serviceDivisionGroups,
        emergencyContact,
        participant,
        acceptDataPrivacy,
        acceptTakePhotos,
        authOfferProducts
      } = form.getValues();
      const { id: participantId, ...rest } = participant;
      const consumers = userIsParticipant
        ? [
            {
              ...user,
              birthdate: user.birthdate?.toISOString(),
              acceptDataPrivacy,
              acceptTakePhotos,
              authOfferProducts,
              isOwnUser: true,
              emergencyContact
            }
          ]
        : [
            {
              ...rest,
              birthdate: participant?.birthdate?.toISOString(),
              acceptDataPrivacy,
              acceptTakePhotos,
              authOfferProducts,
              emergencyContact,
              parents: [user]
            }
          ];

      const payload = {
        service: service.id,
        email: user.email,
        phone: user.phone,
        firstName: user.firstName,
        lastName: user.lastName,
        identification: user.identification,
        location: user.location,
        postalCode: user.postalCode,
        address: user.address,
        serviceDivisionGroups,
        emergencyContact,
        consumers: participantId ? [participantId] : consumers,
        paymentType: 'ef75aed8-40fb-489f-963c-11722aa12440'
      };
      mutate(payload);
      setIsSaleCreated(true);
    }
  };

  const userEmailField = form.watch('user.email');

  const handleBlur = async () => {
    const isEmailValid = await form.trigger('user.email');
    if (userEmailField !== '' && isEmailValid) {
      userEmailRef.current = userEmailField;
      checkUserEmail();
    }
  };

  useEffect(() => {
    const isExpired = isDataExpired();
    if (isExpired) {
      cleanStepServicesData();
    }
    if (purchaseCompleted) {
      setPurchaseCompleted(false);
      cleanStepServicesData();
    }

    Mixpanel.track(`[${service?.name}]: Starting new booking`, {
      screen: 'New Booking Landing',
      Activity: service?.name
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSuccess && isUserExists && !user?.email) {
      setIsOpenRegisteredUserModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt]);

  return {
    form,
    user,
    isPendingSale,
    isOpenSummaryModal,
    isSaleCreated,
    mutate,
    onSubmit,
    setIsOpenSummaryModal,
    setIsSaleCreated,
    handleBlur,
    emailHint,
    isOpenRegisteredUserModal,
    setIsOpenRegisteredUserModal
  };
};
